import {
  Arg,
  COMPONENT_STATE_ARGS,
  FLEX_ARGS,
  Metadata,
  pick,
} from '../../../../utils/spds/metadata';
import { CENTER, SPACE_BETWEEN } from '../../../../utils/spds/constants/css-layout';
import {
  TOAST_DEFAULT_TIMEOUT,
  TOAST_ICONS,
  TOAST_SUPPORTED_TYPES,
} from '../../../../utils/spds/constants/feedback';
import { fn } from '@ember/helper';
import { isEmpty } from '@ember/utils';
import { modifier } from 'ember-modifier';
import { on } from '@ember/modifier';
import { or } from 'ember-truth-helpers';
import { tracked } from '@glimmer/tracking';
import { waitForNext } from '../../../../utils/waiters';
import CloseButton from '../../action/button/close.gjs';
import FlexRow from '../../layout/flex/row.gjs';
import GhostButton from '../../action/button/ghost.gjs';
import Super from '../../core/popover.gjs';
import styles from './message.module.scss';
import svgJar from 'ember-svg-jar/helpers/svg-jar';

const METADATA = Metadata.define({
  description: `
Individual toast notification message rendered as a \`popover="manual"\` element so each toast lives in the browser top layer above modals and other layered UI.

The toast data object is passed as the \`@toast\` argument and contains type, message, and optional cta, alignItems, flex, and gap properties. Stacking position is controlled by \`@offset\` (any CSS length); entry/exit animations are gated by \`@animated\`.

A toast that dismisses itself shows the time it has left as a wedge behind the close button, sweeping away clockwise over its \`timeout\`. It appears on the toast the container is actually counting down — the one at the front of the queue — so a toast raised behind another shows nothing until its turn comes. A \`timeout\` of \`0\` shows nothing at all, and neither does \`@animated={{false}}\`.

The \`Spds::Feedback::Toast::Container\` component must be present at the root of the application to handle toast service events; it manages registration, ordering, and offset computation for each rendered Message.
  `,
  extends: [
    pick(COMPONENT_STATE_ARGS, [{ name: 'animated', defaultValue: true, private: true }]),
    pick(FLEX_ARGS, [
      { name: 'alignItems', default: CENTER, private: true },
      { name: 'flex', default: '1 0 auto', private: true },
      { name: 'gap', default: '16px', private: true },
      { name: 'justifyContent', default: SPACE_BETWEEN, private: true },
    ]),
  ],
  meta: {
    index: Arg.number({
      description: 'Position index of the toast in the container',
      private: true,
    }),
    offset: Arg.string({
      default: '16px',
      description:
        'Distance from the bottom of the viewport to the bottom of this toast (any CSS length unit, e.g. `16px`, `1rem`, `2vh`).',
      private: true,
    }),
    onManualDismiss: Arg.action({
      description: 'Action triggered when the toast is manually dismissed',
    }),
    onReady: Arg.action({
      description:
        'Internal callback fired from `setup` once the popover has been opened so the container can recompute stacking offsets against a known DOM size.',
      private: true,
    }),
    toast: Arg.object({
      description: 'Toast data object containing type, message, and optional cta properties',
    })
      .required()
      .properties({
        cta: Arg.object({ description: 'Optional CTA button configuration' }).properties({
          action: Arg.action({
            description: 'Action to perform when the CTA button is clicked',
          }).required(),
          text: Arg.string({ description: 'CTA button text', initialValue: 'Undo' }).required(),
        }),
        message: Arg.string({
          description: 'Toast message text',
          initialValue: 'An example toast',
        }).required(),
        timeout: Arg.number({
          default: TOAST_DEFAULT_TIMEOUT,
          description: 'Toast timeout in milliseconds',
        }),
        type: Arg.oneOf(TOAST_SUPPORTED_TYPES, {
          description: 'Visual style variant of the toast',
        }).required(),
      }),
  },
  name: 'Toast Message',
  resources: {
    figma:
      'https://www.figma.com/design/g4zf0Vkj39VcCyleokv2rl/%E2%9D%96-Air-web-components?node-id=23480-130662&m=dev',
    zeroheight: 'https://www.simplepractice.design/1d0abbc0a/p/128371-toast',
  },
});

export default class SpdsFeedbackToastMessage extends Super {
  static metadata = this.setMetadata(METADATA, import.meta.url);
  baseClass = 'spds-feedback-toast-message';
  propsToMap = [
    'alignItems',
    'countdownDelay',
    'countdownDuration',
    'flex',
    'gap',
    'justifyContent',
    'offset',
  ];
  styles = styles;

  get classesToMap() {
    return [...super.classesToMap, 'countingDown', 'hasCta', 'open', 'type'];
  }

  /**
   * How far into its countdown the toast already is, as a negative CSS delay, which is what starts
   * the close button's indicator mid-sweep instead of from full. Republished whenever the animation
   * would otherwise restart from nothing, since moving a node to a new parent restarts it.
   */
  @tracked countdownDelay = null;

  /**
   * The component's own record of the popover state, and the class the stylesheet animates off. A
   * toast is only ever rendered to be shown, so it starts open: the class is on the element for its
   * first style resolution, which is what the entry animation's `@starting-style` needs, and
   * `setup` opening the popover a moment later agrees with it rather than correcting it.
   */
  @tracked open = true;

  get alignItems() {
    return this.argOrDefault('alignItems');
  }

  get countdownDuration() {
    return this.countingDown ? `${this.#timeout}ms` : null;
  }

  /**
   * Whether this toast is the one counting down, which is what the close button's indicator shows.
   *
   * The container dismisses toasts in the order they arrived and runs one countdown at a time, so
   * the toast at the front of the queue is the only one whose timer is running. That is read off
   * `@index` here rather than being handed over — a second reading of the container's rule, which
   * only holds while the rule is "the toast in front counts down".
   *
   * `@animated` covers the rest of what the container checks before it starts a timer: it is false
   * under test and while a reparent is in flight, neither of which is counting down.
   *
   * Whether the browser can draw the thing is not asked here. The stylesheet settles that on its
   * own: without the property registration the indicator has nothing to paint with and paints
   * nothing.
   */
  get countingDown() {
    return Boolean(this.animated) && (this.args.index ?? 0) === 0 && this.#timeout > 0;
  }

  get flex() {
    return this.argOrDefault('flex');
  }

  get gap() {
    return this.argOrDefault('gap');
  }

  get hasCta() {
    return !isEmpty(this.toast.cta);
  }

  get justifyContent() {
    return this.argOrDefault('justifyContent');
  }

  get offset() {
    return this.argOrDefault('offset');
  }

  get toast() {
    return this.args.toast;
  }

  get type() {
    return this.toast.type;
  }

  get icon() {
    return TOAST_ICONS[this.type];
  }

  /** How long the toast has before the container dismisses it, defaulted as `manageTimer` does. */
  get #timeout() {
    let { timeout = TOAST_DEFAULT_TIMEOUT } = this.toast;

    return timeout;
  }

  ctaClick = async () => {
    let shouldClose = await Promise.resolve(this.toast.cta.action?.());

    if (shouldClose || isEmpty(shouldClose)) {
      this.args.onManualDismiss?.(this.toast);
    }
  };

  /**
   * Closes the popover and records it, so the stylesheet can animate the toast out. Called by the
   * container, which owns dismissal and reparenting.
   */
  hidePopover() {
    let closed = super.hidePopover();

    if (this.open) this.open = false;

    return closed;
  }

  /**
   * Reopens the popover after the container has moved the node, which drops it out of the top
   * layer. Only assigns when the state actually changes: `setup` calls this during render, where a
   * redundant write to a property the class list has already read would be a backtracking error.
   */
  showPopover() {
    let opened = super.showPopover();

    if (opened && !this.open) this.open = true;

    return opened;
  }

  /**
   * Opens the popover synchronously when the `setupComponent` modifier installs (the element is in
   * the DOM at that point) and notifies the container so it can recompute stacking offsets with
   * this message's known dimensions. The base class guards the call because rapid teardown can
   * remove the element mid-render, and the native call throws on a disconnected popover element.
   */
  setup() {
    super.setup();

    if (!this.baseElement?.isConnected) return;

    this.showPopover();

    this.args.onReady?.(this);
  }

  /**
   * Starts the close button's countdown indicator when the toast reaches the front of the queue.
   * Re-runs on its own whenever that changes, since `countingDown` reads tracked arguments — which
   * is also what covers a move into or out of a modal: `@animated` goes false and back for the
   * duration, and the fresh delay published on the way back resumes an animation that the move
   * restarted from nothing.
   *
   * The moment the countdown started is stamped on the toast rather than on this component,
   * alongside `dismissing`. The countdown belongs to the toast, which outlives the move; the
   * component does not.
   *
   * The delay is published a tick later because a modifier runs inside the render pass that has
   * already read the element's style attribute.
   */
  trackCountdown = modifier(async () => {
    if (!this.countingDown) return;

    let startedAt = (this.toast.countdownStartedAt ??= Date.now());

    await waitForNext();

    if (this.isDestroying || this.isDestroyed) return;

    this.countdownDelay = `-${Date.now() - startedAt}ms`;
  });

  <template>
    <FlexRow
      @alignItems={{this.alignItems}}
      @flex={{this.flex}}
      @gap={{this.gap}}
      @justifyContent={{this.justifyContent}}
      aria-live="assertive"
      class={{this.classes}}
      data-test-spds-feedback-toast-message={{or @index "0"}}
      popover="manual"
      role="status"
      style={{this.customProperties}}
      {{this.setupComponent}}
      {{this.trackCountdown}}
      {{this.validateArguments}}
      ...attributes
    >
      {{#if this.icon}}
        {{svgJar this.icon class="icon" data-test-spds-feedback-toast-message-icon=""}}
      {{/if}}
      <FlexRow @flex="1 0 auto" class="text" data-test-spds-feedback-toast-message-text>
        {{@toast.message}}
      </FlexRow>
      {{#if @toast.cta}}
        <GhostButton
          class="cta-btn"
          data-test-spds-feedback-toast-message-cta-button
          type="button"
          {{on "click" this.ctaClick}}
        >
          {{@toast.cta.text}}
        </GhostButton>
      {{/if}}
      <CloseButton
        @close={{fn @onManualDismiss @toast}}
        class="dismissal-btn"
        data-test-spds-feedback-toast-message-close-button
      />
    </FlexRow>
  </template>
}

export const TOAST_MESSAGE_METADATA = SpdsFeedbackToastMessage.metadata;
